import React, { useMemo, useState } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, Chip, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { PickerHeader } from "components/Prompts/Deal-content-picker/components/PickerHeader";
import { usePrompt } from "hooks";
import { useDealActions } from "store";
import { IFolder } from "store/types";

import { Create } from "../Create";
import { Folder } from "./Folder";

const DIVIDER = "1px solid #E5E8EF";
const SHADOW = "0px 10px 10px -5px rgba(18, 18, 23, 0.04), 0px 20px 25px -5px rgba(18, 18, 23, 0.10)";

interface Props {
  dealId?: number;
  title?: string;
  folders: IFolder[];
  selectedFolderId: number | null;
  onSubmit: (folder: IFolder) => void;
  onCancel: () => void;
  onFolderCreate: (folder: IFolder) => void;
}

export const SelectDealFolder: React.FC<Props> = ({
  dealId,
  title = "Select a Folder",
  folders,
  onFolderCreate,
  selectedFolderId,
  onSubmit,
  onCancel
}) => {
  const { createFolderAction } = useDealActions();
  const openPrompt = usePrompt();

  const [selectedFolder, setSelectedFolder] = useState<IFolder | null>(null);
  const [foldersLocalState, setFoldersLocalState] = useState<IFolder[]>(folders);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSelectFolder = (folder: IFolder) => {
    setSelectedFolder((prev) => (prev?.id === folder.id ? null : folder));
  };

  const handleCreateFolder = async () => {
    const title = await openPrompt({
      Component: Create,
      props: {
        title: "Create New Folder",
        label: "Folder name",
        submitButtonTitle: "Create"
      }
    });
    if (!title) return;
    const newFolder = await createFolderAction({ dealId, title: title, parent_id: selectedFolder?.id });
    setFoldersLocalState((prev) => [...(prev || []), newFolder]);
    setSelectedFolder(newFolder);
    onFolderCreate(newFolder);
  };

  const parentFolders = useMemo(() => {
    return foldersLocalState.filter((folder) => !folder.parent_id);
  }, [foldersLocalState]);

  const handleSubmit = () => {
    if (selectedFolder) {
      onSubmit(selectedFolder);
    }
  };

  return (
    <Box
      sx={{
        width: 1100,
        maxHeight: "calc(100vh - 48px)",
        background: "#fff",
        borderRadius: "12px",
        border: DIVIDER,
        boxShadow: SHADOW,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        outline: "none"
      }}
    >
      <PickerHeader title={title} onClose={onCancel} />

      {/* Search */}
      <Stack direction="row" alignItems="center" gap="12px" sx={{ px: "20px", py: "12px" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search folder..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ "& .MuiInputBase-root": { height: 32 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
              </InputAdornment>
            )
          }}
        />
      </Stack>

      {/* Content area */}
      <Box sx={{ minHeight: 360, overflowY: "auto", flex: 1, background: "#fff" }}>
        <Box sx={{ border: DIVIDER, borderRadius: "8px", mx: "20px", my: "16px" }}>
          {parentFolders.map((folder) => (
            <Folder
              key={folder.id}
              folder={folder}
              selectedFolderId={selectedFolder?.id || selectedFolderId}
              onSelectFolder={handleSelectFolder}
              folders={foldersLocalState}
              searchQuery={searchQuery}
            />
          ))}
          {parentFolders.length === 0 && (
            <Box sx={{ py: "56px", px: "20px", textAlign: "center" }}>
              <FolderOutlinedIcon sx={{ fontSize: 32, color: "text.disabled", display: "block", mx: "auto", mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No folders in workspace</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Add Folder */}
      <Box sx={{ px: "20px", py: "10px" }}>
        <Chip
          icon={<AddCircleOutlineIcon sx={{ fontSize: 14 }} />}
          label="Add Folder"
          onClick={handleCreateFolder}
          size="small"
          sx={{ bgcolor: "#f6f8fc" }}
        />
      </Box>

      {/* Footer */}
      <Stack direction="row" alignItems="center" justifyContent="flex-end" gap="10px" sx={{ px: "20px", py: "14px" }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!selectedFolder} variant="contained" onClick={handleSubmit}>
          Select
        </Button>
      </Stack>
    </Box>
  );
};
